import { Loading } from "../../app/Status";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import {
  ACTIVE_STATUSES,
  ageRatio,
  theoreticalScore,
  todayIso,
  MIN_ROOM_AREA,
  PRIO,
  PRIORITIES,
  polyArea,
  floorAreas,
  formatM2,
  formatMeters,
  scaleDeviation,
  roomAt,
  suggestName,
  assetTypeLabel,
  nlsfbLabel,
  OBLIGATION_STATUS_LABELS,
  type AssetDetail,
  type AssetDto,
  type AssetKind,
  type BuildingDto,
  type IssueDetail,
  type TaskDto,
  type RoomDto,
  type ScaleLine,
} from "@klantpunt/shared";
import { api, ApiError } from "../../lib/api";
import { can, hasModule, useMe } from "../../lib/auth";
import { fileInfo, isPdf, isPlanImage } from "../../lib/pdf";
import { useToast } from "../../lib/toast";
import { aspectDiff, uploadPlan } from "../../lib/upload";
import { useRealtime } from "../../lib/realtime";
import { PHONE_QUERY, useMediaQuery } from "../../lib/useMediaQuery";
import { Modal } from "../../app/Modal";
import { ToolPopover } from "../../app/ToolPopover";
import { Segmented, type ChoiceItem } from "../../app/Choice";
import { countByPriority, DEFAULT_FILTERS, filterIssues, type Filters } from "../issues/filters";
import { IssueDetailPanel } from "../issues/IssueDetailPanel";
import { IssueForm } from "../issues/IssueForm";
import { IssueListPane, IssueTable } from "../issues/IssueList";
import { AddLocationDialog, PickIssueDialog } from "../issues/LocationDialogs";
import { useIssueCache, useIssues } from "../issues/useIssues";
import { TaskBoard } from "../tasks/TaskBoard";
import { TaskDialog } from "../tasks/TaskDialog";
import { useTasks } from "../tasks/useTasks";
import { newBatch, newDraw, newScale, toPoints, type DrawState, type Point, type SetScale } from "../plan/drawState";
import { PlanStage, requestZoom, ZMAX, ZMIN, type AssetMarker, type PinItem } from "../plan/PlanStage";
import { AssetDialog } from "../assets/AssetDialog";
import { AssetList } from "../assets/AssetList";
import { ObligationList } from "../obligations/ObligationList";
import { AssetPanel, assetKey, type AssetActions } from "../assets/AssetPanel";
import { BUILDING_OWNER, BuildingObligationsPanel } from "../obligations/BuildingObligationsPanel";
import { AddRoomDialog, BuildingDialog, FloorDialog, RoomDialog } from "./dialogs";
import { ImportDialog } from "./ImportDialog";
import { FloorLift, PlaceBar } from "./PlaceBar";
import { MeterRoundCard } from "../meters/MeterRoundCard";
import { buildingKey, useBuilding, useBuildingCache } from "./useBuilding";
import { NEW_ISSUE_EVENT } from "../../app/Layout";
import { buildingPath, isBuildingSection, type BuildingSection } from "../../app/navItems";
import { IconAssets, IconBuilding, IconLayers, IconFile, IconMore, IconZoomIn, IconZoomOut } from "../../app/icons";
import { ReportDialog } from "../report/ReportDialog";
import { isLocal } from "../../offline/ops";
import { usePrefetchPlans } from "../../offline/useOffline";

type ModalState =
  | { type: "building" }
  | { type: "floor"; id?: string }
  | { type: "addRoom" }
  | { type: "room"; id: string }
  | {
      type: "asset";
      id?: string;
      initial?: {
        floorId?: string | null;
        roomId?: string | null;
        x?: number;
        y?: number;
        assetKind?: AssetKind;
        elementCode?: string | null;
        parentAssetId?: string;
      };
    }
  | { type: "import"; files: File[] }
  | {
      type: "issue";
      issue?: IssueDetail;
      initial?: {
        floorId?: string;
        roomId?: string | null;
        pin?: { x: number; y: number } | null;
        installationId?: string;
      };
    }
  | { type: "addLocation"; issue: IssueDetail }
  | { type: "pickIssue"; loc: { floorId: string; roomId: string | null; x: number; y: number } }
  | { type: "task"; task?: TaskDto; forIssue?: string }
  | { type: "report" }
  | null;

/** Volgende kopie in de rij om te plaatsen, of klaar. */
function nextCopy(queue: PlaceItem[], step?: { n: number; total: number }): TapMode {
  const [next, ...rest] = queue;
  return next
    ? { kind: "placeAsset", asset: next, queue: rest, step: step && { n: step.n + 1, total: step.total } }
    : null;
}

/** Asset om te plaatsen; `floorId`: de verdieping waar hij al staat (daar opent de tekening). */
type PlaceItem = { id: string; code: string; name?: string; floorId?: string | null };

/** Wat de volgende tik op de tekening doet. */
type TapMode =
  | { kind: "pin" }
  | { kind: "move"; issue: IssueDetail }
  | { kind: "addloc"; issue: IssueDetail }
  | {
      kind: "placeAsset";
      asset: PlaceItem;
      /** Na dupliceren of een bulkactie: de assets die hierna nog geplaatst worden, en de voortgang. */
      queue?: PlaceItem[];
      step?: { n: number; total: number };
    }
  | null;

/** Lagen op de tekening (per apparaat onthouden). */
type Layers = { issues: boolean; assets: boolean; meters: boolean };
type View = "plan" | "table" | "tasks" | "assets";
/** Weergave per onderdeel uit het menu; Assets, Arbeidsmiddelen en Onderhoud en keuringen delen de assetweergave. */
type Section = Exclude<BuildingSection, "overzicht" | "gebouw" | "dossier" | "materialen" | "contracten">;
const VIEW_OF: Record<Section, View> = {
  tekening: "plan",
  bevindingen: "table",
  taken: "tasks",
  assets: "assets",
  arbeidsmiddelen: "assets",
  meters: "assets",
  keuringen: "assets",
};
const SECTION_OF: Record<View, BuildingSection> = {
  plan: "tekening",
  table: "bevindingen",
  tasks: "taken",
  assets: "assets",
};
/** Navigatie binnen de pagina zelf (Toon op tekening, filter op element): de selectie blijft staan. */
const KEEP = { keep: true };
const SECTION_TITLE: Record<Section, string> = {
  tekening: "Tekening",
  bevindingen: "Bevindingen",
  taken: "Taken",
  assets: "Assets",
  arbeidsmiddelen: "Arbeidsmiddelen",
  meters: "Meters",
  keuringen: "Onderhoud en keuringen",
};
/** Lijsten met de plek (verdieping en ruimte) erboven. */
const PLACE_SECTIONS: ReadonlySet<Section> = new Set([
  "bevindingen",
  "assets",
  "arbeidsmiddelen",
  "meters",
  "keuringen",
]);
const LAYERS_KEY = "pw-layers";
const readLayers = (): Layers => {
  try {
    return {
      issues: true,
      assets: true,
      meters: true,
      ...(JSON.parse(localStorage.getItem(LAYERS_KEY) ?? "{}") as Partial<Layers>),
    };
  } catch {
    return { issues: true, assets: true, meters: true };
  }
};

const floorKey = (id: string) => `pw-floor-${id}`;
const roomKey = (id: string) => `pw-room-${id}`;
const remember = (key: string, value: string | null) => {
  try {
    if (value) sessionStorage.setItem(key, value);
    else sessionStorage.removeItem(key);
  } catch {
    /* geen opslag */
  }
};
const errMsg = (e: unknown) => (e instanceof ApiError ? e.message : "Er ging iets mis");
const hasFiles = (e: DragEvent) => [...(e.dataTransfer?.types ?? [])].includes("Files");

export function BuildingPage() {
  const { id = "", section: sectionParam } = useParams();
  const section: Section =
    isBuildingSection(sectionParam) &&
    sectionParam !== "overzicht" &&
    sectionParam !== "gebouw" &&
    sectionParam !== "dossier" &&
    sectionParam !== "materialen" &&
    sectionParam !== "contracten"
      ? sectionParam
      : "tekening";
  const location = useLocation();
  const { data: me } = useMe();
  const { data: b, isPending, error, fetchStatus } = useBuilding(id);
  const cache = useBuildingCache(id);
  const toast = useToast();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const canManage = can(me, "building.manage");
  const canUpload = can(me, "plan.upload");
  const canCreate = can(me, "issue.create");
  const canEditIssue = can(me, "issue.edit");
  const canReport = can(me, "report.view");
  // Modules (docs/ASSETS-MJOP.md): zonder Bevindingen of Assets geen tabbladen, lagen of knoppen.
  const seeIssues = hasModule(me, "issues");
  const seeAssets = hasModule(me, "assets");
  const seeCondition = hasModule(me, "condition");
  /** Meters zijn een eigen laag op de tekening zodra de module aan staat; anders vallen ze onder Assets. */
  const meterLayer = seeAssets && hasModule(me, "meters");
  const issuesQ = useIssues(id, seeIssues);
  const issueCache = useIssueCache(id);
  useRealtime(id);
  usePrefetchPlans(b?.floors.flatMap((x) => (x.plan ? [x.plan.id] : [])) ?? []);
  const [params, setParams] = useSearchParams();

  const tasksQ = useTasks(id, seeIssues);
  const allTasks = useMemo(() => tasksQ.data ?? [], [tasksQ.data]);
  const phoneNow = useMediaQuery(PHONE_QUERY);
  // Op de telefoon is Bevindingen de lijst naast de tekening (paneel "list"), niet de tabel.
  const viewRaw: View = phoneNow && section === "bevindingen" ? "plan" : VIEW_OF[section];
  const setView = (v: View) => {
    const next = SECTION_OF[v];
    if (next !== section) navigate(buildingPath(id, next), { state: KEEP });
  };
  // Weergave zonder recht op de module valt terug op de tekening.
  const view =
    ((viewRaw === "table" || viewRaw === "tasks") && !seeIssues) ||
    (viewRaw === "assets" && !seeAssets) ||
    (section === "meters" && !hasModule(me, "meters"))
      ? "plan"
      : viewRaw;
  const [selAsset, setSelAsset] = useState<string | null>(null);
  const [layers, setLayersRaw] = useState<Layers>(readLayers);
  /** Assetlaag: alleen assets met een verlopen of bijna verlopen keuring. */
  const [onlyDue, setOnlyDue] = useState(false);
  /** Kaartlaag conditie (docs/CONDITIEMETING.md §10.6): uit, gemeten of theoretisch. */
  const [condView, setCondView] = useState<"off" | "measured" | "theoretical">("off");
  /** Assetweergave: de lijst (assets of arbeidsmiddelen) of de keuringen. */
  const assetMode = section === "keuringen" ? "inspections" : "list";
  const setLayers = (l: Layers) => {
    setLayersRaw(l);
    try {
      localStorage.setItem(LAYERS_KEY, JSON.stringify(l));
    } catch {
      /* geen opslag */
    }
  };
  const canAsset = can(me, "asset.manage");
  const [mpane, setMpane] = useState<"plan" | "list">(() => (section === "bevindingen" ? "list" : "plan"));
  const [f, setF] = useState<Filters>(DEFAULT_FILTERS);
  const [sel, setSel] = useState<string | null>(null);
  const [hl, setHl] = useState<string | null>(null);
  const [tapMode, setTapMode] = useState<TapMode>(null);
  const [mapMenu, setMapMenu] = useState<{ x: number; y: number; roomId: string | null } | null>(null);
  const [focus, setFocus] = useState<{ x: number; y: number; n: number } | null>(null);

  const [floorId, setFloorIdRaw] = useState<string>(() => sessionStorage.getItem(floorKey(id)) ?? "");
  /** Ruimte van de plek boven de lijsten (null: alle ruimtes); hoort bij de gekozen verdieping. */
  const [roomF, setRoomFRaw] = useState<string | null>(() => sessionStorage.getItem(roomKey(id)));
  const setRoomF = useCallback(
    (rid: string | null) => {
      setRoomFRaw(rid);
      remember(roomKey(id), rid);
    },
    [id],
  );
  const [zoom, setZoom] = useState(1);
  const [showRooms, setShowRooms] = useState(false);
  const [draw, setDraw] = useState<DrawState | null>(null);
  const [modal, setModal] = useState<ModalState>(null);
  const phone = useMediaQuery(PHONE_QUERY);
  const [viewSheet, setViewSheet] = useState(false);
  const viewSheetId = useId();
  // Desktop: Weergave en ⋯ als paneel onder de knop in de werkbalk.
  const [viewPop, setViewPop] = useState(false);
  const [actionsPop, setActionsPop] = useState(false);
  const closeViewSheet = () => {
    setViewSheet(false);
    setActionsPop(false);
  };
  const [dropCover, setDropCover] = useState(false);
  const replaceInput = useRef<HTMLInputElement>(null);
  const nameInput = useRef<HTMLInputElement>(null);
  const metersInput = useRef<HTMLInputElement>(null);
  /** Verhouding van het getoonde blad (hoogte/breedte), voor de schaal. */
  const [planAspect, setPlanAspect] = useState<number | null>(null);

  const setFloorId = useCallback(
    (fid: string) => {
      setFloorIdRaw(fid);
      // Wie een verdieping kiest, ziet in de lijsten (ook Bevindingen en Assets) die verdieping.
      setF((cur) => (cur.scope === "floor" ? cur : { ...cur, scope: "floor" }));
      setZoom(1);
      setDraw(null);
      setMapMenu(null);
      remember(floorKey(id), fid);
      // Een ruimte op een andere verdieping valt weg.
      if (b?.rooms.find((r) => r.id === roomF)?.floorId !== fid) setRoomF(null);
    },
    [id, b, roomF, setRoomF],
  );

  // Geldige verdieping kiezen als de huidige niet (meer) bestaat.
  useEffect(() => {
    if (b && !b.floors.some((f) => f.id === floorId)) setFloorIdRaw(b.floors[0]?.id ?? "");
  }, [b, floorId]);

  // Slepen-overal-op-de-pagina: tekeningen importeren.
  useEffect(() => {
    if (!canManage || !canUpload) return;
    let depth = 0;
    const enter = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      depth++;
      setDropCover(true);
    };
    const over = (e: DragEvent) => hasFiles(e) && e.preventDefault();
    const leave = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      depth = Math.max(0, depth - 1);
      if (!depth) setDropCover(false);
    };
    const drop = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth = 0;
      setDropCover(false);
      const files = [...(e.dataTransfer?.files ?? [])];
      if (!files.length) return;
      setModal((m) => {
        if (m?.type === "import") {
          window.dispatchEvent(new CustomEvent("pw-import-files", { detail: files }));
          return m;
        }
        return m ? m : { type: "import", files };
      });
    };
    // Altijd opruimen, ook als het importvenster de drop zelf afhandelt (stopPropagation) of het slepen stopt.
    const reset = () => {
      depth = 0;
      setDropCover(false);
    };
    document.addEventListener("dragenter", enter);
    document.addEventListener("dragover", over);
    document.addEventListener("dragleave", leave);
    document.addEventListener("drop", drop);
    document.addEventListener("drop", reset, true);
    document.addEventListener("dragend", reset);
    window.addEventListener("blur", reset);
    return () => {
      document.removeEventListener("dragenter", enter);
      document.removeEventListener("dragover", over);
      document.removeEventListener("dragleave", leave);
      document.removeEventListener("drop", drop);
      document.removeEventListener("drop", reset, true);
      document.removeEventListener("dragend", reset);
      window.removeEventListener("blur", reset);
    };
  }, [canManage, canUpload]);

  const floor = b?.floors.find((x) => x.id === floorId);
  const floorRooms = useMemo(() => b?.rooms.filter((r) => r.floorId === floorId) ?? [], [b, floorId]);
  // Oppervlakte per ruimte: met de schaal die nu wordt ingesteld, anders die van de verdieping.
  const scaleLines = draw?.kind === "scale" ? draw.lines : null;
  const areas = useMemo(() => {
    const sc =
      scaleLines && planAspect
        ? scaleLines.length
          ? { aspect: planAspect, lines: scaleLines }
          : null
        : (floor?.scale ?? null);
    return sc ? floorAreas(sc, floorRooms) : null;
  }, [scaleLines, planAspect, floor?.scale, floorRooms]);
  const allIssues = useMemo(() => issuesQ.data ?? [], [issuesQ.data]);
  const counts = countByPriority(allIssues);
  // Plek boven de lijsten: verdieping (of alle) en ruimte.
  const placeFloor = f.scope === "floor" && floorId ? floorId : null;
  const placeRoom = placeFloor && b?.rooms.some((r) => r.id === roomF && r.floorId === placeFloor) ? roomF : null;
  const room = placeRoom ? b?.rooms.find((r) => r.id === placeRoom) : undefined;
  const placeLabel = floor && placeFloor ? (room ? `${floor.name} › ${room.name}` : floor.name) : undefined;
  const listForPane = useMemo(
    () => (b ? filterIssues(allIssues, f, b, placeFloor, placeRoom) : []),
    [allIssues, f, b, placeFloor, placeRoom],
  );
  /** Open (actieve) bevindingen per verdieping en per ruimte, voor het liftpaneel en de kiezers. */
  const openCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const i of allIssues) {
      if (!ACTIVE_STATUSES.has(i.status)) continue;
      const ids = new Set(i.locations.flatMap((l) => [l.floorId, ...(l.roomId ? [l.roomId] : [])]));
      ids.forEach((k) => m.set(k, (m.get(k) ?? 0) + 1));
    }
    return m;
  }, [allIssues]);
  const pickPlaceFloor = (fid: string | null) => {
    if (fid) setFloorId(fid);
    else setF((cur) => ({ ...cur, scope: "all" }));
    setRoomF(null);
  };
  const pickPlaceRoom = (rid: string | null) => {
    const r = b?.rooms.find((x) => x.id === rid);
    if (r && r.floorId !== floorId) setFloorId(r.floorId);
    if (r) setF((cur) => (cur.scope === "floor" ? cur : { ...cur, scope: "floor" }));
    setRoomF(r ? r.id : null);
  };
  const assetInPlace = (aid: string) => {
    const a = b?.assets.find((x) => x.id === aid);
    return Boolean(a && (!placeFloor || a.floorId === placeFloor) && (!placeRoom || a.roomId === placeRoom));
  };

  // Pins: actief altijd; afgerond alleen met statusfilter ≠ Actief (of geselecteerd).
  const pins = useMemo<PinItem[]>(() => {
    const out: PinItem[] = [];
    for (const i of allIssues) {
      const active = ACTIVE_STATUSES.has(i.status);
      if (!(f.status !== "actief" || active || i.id === sel || i.id === hl)) continue;
      const n = i.locations.length;
      i.locations.forEach((l, k) => {
        if (l.floorId !== floorId || l.x == null || l.y == null) return;
        out.push({
          key: l.id,
          issueId: i.id,
          x: l.x,
          y: l.y,
          seq: i.seq,
          priority: active ? i.priority : "done",
          extra: k > 0,
          done: !active,
          selected: i.id === sel || i.id === hl,
          label: `${i.code} · ${i.title}${n > 1 ? ` (plek ${k + 1} van ${n})` : ""}`,
        });
      });
    }
    return out;
  }, [allIssues, f.status, floorId, sel, hl]);

  const selRow = selAsset ? b?.assets.find((a) => a.id === selAsset) : undefined;
  /** Hoofdasset van de gekozen asset (of zichzelf); null als er geen onderdelen bij horen. */
  const selFamily = selRow
    ? (selRow.parentAssetId ?? (b?.assets.some((a) => a.parentAssetId === selRow.id) ? selRow.id : null))
    : null;
  const assetMarkers = useMemo<AssetMarker[]>(
    () =>
      seeAssets && b
        ? b.assets
            .filter((a) => a.floorId === floorId && a.x != null && a.y != null)
            .filter((a) => (meterLayer && a.assetKind === "meter" ? layers.meters : layers.assets))
            .filter((a) => !onlyDue || a.obligationStatus === "overdue" || a.obligationStatus === "soon")
            .map((a) => ({
              id: a.id,
              x: a.x!,
              y: a.y!,
              elementCode: a.elementCode,
              label: `${a.code} · ${a.name} (${assetTypeLabel(a.assetType) ?? nlsfbLabel(a.elementCode)})${
                a.obligationStatus ? ` · keuring: ${OBLIGATION_STATUS_LABELS[a.obligationStatus].toLowerCase()}` : ""
              }`,
              selected: a.id === selAsset,
              // Bij een samengestelde asset de hoofdasset en alle onderdelen markeren.
              related: a.id !== selAsset && !!selFamily && (a.id === selFamily || a.parentAssetId === selFamily),
              status: a.obligationStatus,
              meter: a.assetKind === "meter",
              assetType: a.assetType,
              ...(seeCondition && condView !== "off"
                ? {
                    condition:
                      condView === "measured"
                        ? a.conditionScore
                        : theoreticalScore(ageRatio(a.yearBuilt, a.lifespanYears, todayIso())),
                  }
                : {}),
            }))
            .map((m) =>
              m.condition !== undefined
                ? {
                    ...m,
                    label: `${m.label} · conditie ${condView === "measured" ? "gemeten" : "theoretisch"}: ${m.condition ?? "onbekend"}`,
                  }
                : m,
            )
        : [],
    [
      b,
      floorId,
      layers.assets,
      layers.meters,
      meterLayer,
      seeAssets,
      selAsset,
      selFamily,
      onlyDue,
      seeCondition,
      condView,
    ],
  );

  // QR-code / link: ?asset=<id> opent de asset (op de tekening als hij een plek heeft).
  useEffect(() => {
    const aid = params.get("asset");
    if (!aid || !b) return;
    const a = b.assets.find((x) => x.id === aid);
    if (a?.floorId && a.x != null && a.y != null) {
      setFloorIdRaw(a.floorId);
      setFocus((cur) => ({ x: a.x!, y: a.y!, n: (cur?.n ?? 0) + 1 }));
    }
    setSelAsset(aid);
    setSel(null);
    if (window.innerWidth <= 760) setMpane("list");
    params.delete("asset");
    setParams(params, { replace: true, state: KEEP });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [b?.id]);

  // ?gebouw=1 opent onderhoud en keuringen bij het gebouw zelf (vanuit planning, dossier of materialen).
  useEffect(() => {
    if (params.get("gebouw") !== "1" || !b) return;
    openAsset(BUILDING_OWNER);
    params.delete("gebouw");
    setParams(params, { replace: true, state: KEEP });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [b?.id]);

  // Link vanuit "Mijn taken": ?bevinding=<id> opent het detail.
  useEffect(() => {
    const iid = params.get("bevinding");
    if (iid) {
      setSel(iid);
      setHl(iid);
      if (window.innerWidth <= 760) setMpane("list");
      params.delete("bevinding");
      setParams(params, { replace: true, state: KEEP });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Vanuit de planning: ?taak=<id> opent de taak (wie taken beheert) zodra de taken er zijn.
  useEffect(() => {
    const tid = params.get("taak");
    if (!tid || !tasksQ.data) return;
    const tk = tasksQ.data.find((x) => x.id === tid);
    if (tk && can(me, "task.manage")) setModal({ type: "task", task: tk });
    params.delete("taak");
    setParams(params, { replace: true, state: KEEP });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasksQ.data]);

  // Vanuit Overzicht: ?prio=P1 toont de actieve bevindingen met die prioriteit.
  useEffect(() => {
    const p = params.get("prio");
    if (!p || !(PRIORITIES as readonly string[]).includes(p)) return;
    setF((cur) => ({ ...cur, prio: p, status: "actief", scope: "all" }));
    params.delete("prio");
    setParams(params, { replace: true, state: KEEP });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Vanaf de Gebouw-pagina: ?verdieping=<id> kiest de verdieping, ?teken=<ruimte> of ?teken=alle start het
  // intekenen, ?hoeken=<ruimte> het verplaatsen van de hoekpunten.
  useEffect(() => {
    if (!b) return;
    const fl = params.get("verdieping");
    const teken = params.get("teken");
    const hoeken = params.get("hoeken");
    const schaal = params.get("schaal");
    if (!fl && !teken && !hoeken && !schaal) return;
    const room = b.rooms.find((r) => r.id === (teken ?? hoeken));
    const fid = room?.floorId ?? schaal ?? fl;
    if (fid && b.floors.some((x) => x.id === fid)) setFloorId(fid);
    if (canManage) {
      if (schaal && fid === schaal) setTimeout(() => startScale(schaal), 0);
      else if (teken === "alle") setTimeout(startBatch, 0);
      else if (room && teken) setTimeout(() => setDraw(newDraw(room)), 0);
      else if (room?.shape && hoeken)
        setTimeout(
          () =>
            setDraw({
              kind: "vertices",
              roomId: room.id,
              roomName: room.name,
              points: toPoints(room.shape!.points),
              active: null,
            }),
          0,
        );
    }
    for (const k of ["verdieping", "teken", "hoeken", "schaal"]) params.delete(k);
    setParams(params, { replace: true, state: KEEP });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [b?.id, location.key]);

  // /melden en meld.pandwachters.nl: direct het formulier "Nieuwe bevinding".
  useEffect(() => {
    if (b && params.get("nieuw") === "1") {
      if (canCreate) setModal({ type: "issue", initial: { floorId: floorId || b.floors[0]?.id } });
      params.delete("nieuw");
      setParams(params, { replace: true, state: KEEP });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [b, canCreate]);

  function openIssue(issueId: string) {
    setSelAsset(null);
    setSel(issueId);
    setHl(issueId);
    setMapMenu(null);
    // Op telefoon staat het detail in het lijstpaneel.
    if (window.innerWidth <= 760) setMpane("list");
  }

  // Via het menu naar een ander onderdeel (of opnieuw hetzelfde): het detailpaneel gaat dicht en op de
  // telefoon toont Bevindingen de lijst, de rest de tekening. Navigatie binnen de pagina (KEEP) laat alles staan.
  const firstNav = useRef(true);
  useEffect(() => {
    if (firstNav.current) {
      firstNav.current = false;
      return;
    }
    if ((location.state as typeof KEEP | null)?.keep) return;
    setSel(null);
    setHl(null);
    setSelAsset(null);
    setTapMode(null);
    setMpane(section === "bevindingen" ? "list" : "plan");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  function openAsset(assetId: string) {
    setSel(null);
    setHl(null);
    setSelAsset(assetId);
    setMapMenu(null);
    if (window.innerWidth <= 760) setMpane("list");
  }

  function savedIssue(d: IssueDetail, created: boolean, queued = false) {
    if (!queued) issueCache.put(d);
    setModal(null);
    setMapMenu(null);
    if (queued) toast("Bevinding opgeslagen op dit apparaat; wordt verstuurd zodra er verbinding is");
    else if (created) toast(`${d.code} vastgelegd`);
    else toast(`${d.code} bijgewerkt`);
    openIssue(d.id);
  }

  async function onTap(x: number, y: number) {
    if (!b) return;
    const hit = roomAt(floorRooms, x, y);
    const roomId = hit?.id ?? null;
    if (tapMode?.kind === "move") {
      const issue = tapMode.issue;
      const main = issue.locations[0];
      setTapMode(null);
      try {
        const d = main
          ? await api<IssueDetail>(`/issues/${issue.id}/locations/${main.id}`, {
              method: "PATCH",
              body: { floorId, x, y, roomId: roomId ?? (main.floorId === floorId ? main.roomId : null) },
            })
          : await api<IssueDetail>(`/issues/${issue.id}/locations`, {
              method: "POST",
              body: { floorId, roomId, x, y },
            });
        issueCache.put(d);
        openIssue(d.id);
      } catch (e) {
        toast(errMsg(e));
      }
      return;
    }
    if (tapMode?.kind === "addloc") {
      try {
        const d = await api<IssueDetail>(`/issues/${tapMode.issue.id}/locations`, {
          method: "POST",
          body: { floorId, roomId, x, y },
        });
        issueCache.put(d);
        setTapMode({ kind: "addloc", issue: d });
        toast(`${d.code}: plek toegevoegd (${d.locations.length} in totaal)`);
      } catch (e) {
        toast(errMsg(e));
      }
      return;
    }
    if (tapMode?.kind === "pin") {
      setTapMode(null);
      setModal({ type: "issue", initial: { floorId, roomId, pin: { x, y } } });
      return;
    }
    if (tapMode?.kind === "placeAsset") {
      const { asset: a, queue = [], step } = tapMode;
      setTapMode(nextCopy(queue, step));
      try {
        const d = await api<AssetDetail>(`/assets/${a.id}`, {
          method: "PATCH",
          body: { floorId, x, y, ...(roomId ? { roomId } : {}) },
        });
        qc.setQueryData(assetKey(d.id), d);
        cache.upsertAsset(d);
        toast(`${d.code} staat op de tekening`);
        if (!queue.length) openAsset(d.id);
      } catch (e) {
        toast(errMsg(e));
      }
      return;
    }
    if (mapMenu) {
      setMapMenu(null);
      return;
    }
    if (canCreate || canAsset) setMapMenu({ x, y, roomId });
  }

  // Volgende asset in de rij staat op een andere verdieping: daarheen.
  const placeNext = tapMode?.kind === "placeAsset" ? tapMode.asset : null;
  useEffect(() => {
    if (placeNext?.floorId && placeNext.floorId !== floorId) setFloorId(placeNext.floorId);
    // Alleen bij een nieuwe stap; daarna mag de gebruiker zelf van verdieping wisselen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeNext?.id]);

  // Escape: menu, modus, selectie sluiten.
  useEffect(() => {
    if (modal || draw) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (mapMenu) setMapMenu(null);
      else if (tapMode) setTapMode(null);
      else if (selAsset) setSelAsset(null);
      else if (sel) {
        setSel(null);
        setHl(null);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [modal, draw, mapMenu, tapMode, sel, selAsset]);

  // ---------- intekenen ----------
  async function saveShape(roomId: string, points: Point[], name: string) {
    try {
      const r = await api<RoomDto>(`/rooms/${roomId}`, { method: "PATCH", body: { shape: { type: "poly", points } } });
      cache.upsertRoom(r);
      setDraw(null);
      setShowRooms(true);
      toast(`${name} ingetekend. Tikken binnen dit vlak vult deze ruimte in.`);
    } catch (e) {
      toast(errMsg(e));
    }
  }

  function onShape(points: Point[]) {
    if (!draw || draw.kind !== "draw") return;
    if (polyArea(points) < MIN_ROOM_AREA) {
      toast("Dit vlak is te klein. Zet de punten verder uit elkaar.");
      return;
    }
    if (draw.batch) {
      setDraw({
        ...draw,
        naming: true,
        shape: points,
        nameDraft: suggestName(draw.lastName),
        pts: [],
        rect: null,
        hover: null,
      });
      setTimeout(() => {
        nameInput.current?.focus();
        nameInput.current?.select();
      }, 30);
    } else if (draw.roomId) {
      void saveShape(draw.roomId, points, draw.roomName);
    }
  }

  async function saveBatchRoom() {
    if (!draw || draw.kind !== "draw" || !draw.naming || !draw.shape) return;
    const name = draw.nameDraft.trim();
    if (!name) {
      toast("Geef de ruimte een naam.");
      nameInput.current?.focus();
      return;
    }
    if (floorRooms.some((r) => r.name.toLowerCase() === name.toLowerCase()))
      toast(`Let op: “${name}” bestaat al op deze verdieping.`);
    try {
      const r = await api<RoomDto>(`/floors/${floorId}/rooms`, {
        method: "POST",
        body: { id: crypto.randomUUID(), name, shape: { type: "poly", points: draw.shape } },
      });
      cache.upsertRoom(r);
      setDraw({ ...draw, naming: false, shape: null, nameDraft: "", count: draw.count + 1, lastName: name });
      toast(`${name} toegevoegd. Teken de volgende ruimte.`);
    } catch (e) {
      toast(errMsg(e));
    }
  }

  function finishPoly() {
    if (draw?.kind === "draw" && !draw.naming && draw.pts.length >= 3) onShape(draw.pts);
  }

  // Toetsenbord tijdens intekenen: Enter = klaar, Backspace = punt terug, Escape = stoppen.
  useEffect(() => {
    if (!draw || modal) return;
    const onKey = (e: KeyboardEvent) => {
      const typing = /INPUT|TEXTAREA|SELECT/.test((e.target as HTMLElement).tagName);
      if (e.key === "Escape") {
        setDraw(null);
        return;
      }
      if (typing || draw.kind !== "draw" || draw.mode !== "poly" || draw.naming) return;
      if (e.key === "Enter" && draw.pts.length >= 3) {
        e.preventDefault();
        finishPoly();
      }
      if (e.key === "Backspace" && draw.pts.length) {
        e.preventDefault();
        setDraw({ ...draw, pts: draw.pts.slice(0, -1) });
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });

  // ---------- tekening vervangen / blad kiezen ----------
  async function replacePlan(file: File) {
    if (!floor || !b) return;
    if (!isPdf(file) && !isPlanImage(file)) return toast("Alleen PDF, PNG, JPG of WebP.");
    try {
      const info = await fileInfo(file);
      const diff = aspectDiff(floor.plan, info);
      if (
        diff > 0.02 &&
        !window.confirm(
          `De verhouding van de nieuwe tekening wijkt ${Math.round(diff * 100)} % af. Pins en ruimtes staan relatief en kunnen verschuiven. Toch vervangen?`,
        )
      )
        return;
      toast("Tekening uploaden…");
      const plan = await uploadPlan(file, info);
      cache.set(
        await api<BuildingDto>(`/floors/${floor.id}`, { method: "PATCH", body: { planFileId: plan.id, planPage: 1 } }),
      );
      toast(`Tekening voor ${floor.name} opgeslagen`);
    } catch (e) {
      toast(errMsg(e));
    }
  }

  async function setPage(page: number) {
    if (!floor) return;
    try {
      cache.set(await api<BuildingDto>(`/floors/${floor.id}`, { method: "PATCH", body: { planPage: page } }));
    } catch (e) {
      toast(errMsg(e));
    }
  }

  function startBatch() {
    setTapMode(null);
    setMapMenu(null);
    setDraw((d) => (d?.kind === "draw" && d.batch ? null : newBatch()));
    setShowRooms(true);
  }

  // ---------- schaal (docs/SPEC.md, Oppervlakte) ----------
  function startScale(fid = floorId) {
    const fl = b?.floors.find((x) => x.id === fid);
    if (!fl) return;
    setTapMode(null);
    setMapMenu(null);
    setDraw((d) => (d?.kind === "scale" ? null : newScale(fl.id, fl.scale?.lines ?? [])));
    setShowRooms(true);
  }

  function addScaleLine(d: SetScale) {
    const meters = Number(d.meters.replace(",", ".").trim());
    if (!(meters > 0 && meters <= 10_000)) {
      toast("Vul de werkelijke lengte in meter in, bijvoorbeeld 12,5.");
      metersInput.current?.focus();
      return;
    }
    const [a, bb] = d.pts as [Point, Point];
    if (Math.hypot(a[0] - bb[0], a[1] - bb[1]) < 0.02) {
      toast("De punten liggen te dicht bij elkaar. Kies een zo lang mogelijke maat.");
      return;
    }
    const line: ScaleLine = { a, b: bb, meters };
    setDraw({ ...d, lines: [...d.lines, line], pts: [], meters: "" });
  }

  async function saveScale(d: SetScale) {
    if (!b || !planAspect) return;
    const scale = d.lines.length ? { aspect: planAspect, lines: d.lines } : null;
    try {
      const nb = await api<BuildingDto>(`/floors/${d.floorId}`, { method: "PATCH", body: { scale } });
      cache.set(nb);
      setDraw(null);
      if (!scale) return toast("Schaal gewist");
      const fl = nb.floors.find((x) => x.id === d.floorId);
      const a = floorAreas(
        scale,
        nb.rooms.filter((r) => r.floorId === d.floorId),
      );
      toast(
        a.measured
          ? `Schaal opgeslagen. ${fl?.name ?? "Verdieping"}: ${formatM2(a.total)} aan ingetekende ruimtes.`
          : "Schaal opgeslagen. Teken ruimtes in om hun oppervlakte te zien.",
      );
    } catch (e) {
      toast(errMsg(e));
    }
  }

  // "Bevinding" in het hoofdmenu (onderbalk op telefoon) werkt in dit gebouw.
  const newIssueRef = useRef<() => void>(() => {});
  useEffect(() => {
    const on = () => newIssueRef.current();
    window.addEventListener(NEW_ISSUE_EVENT, on);
    return () => window.removeEventListener(NEW_ISSUE_EVENT, on);
  }, []);

  // Zonder verbinding en niet eerder geopend op dit apparaat.
  if (!b && (fetchStatus === "paused" || (error instanceof ApiError && error.status === 0)))
    return (
      <div className="center">
        <p>
          Dit gebouw staat nog niet op dit apparaat. Open het een keer met verbinding om het offline te kunnen
          gebruiken. <Link to="/">Terug naar gebouwen</Link>
        </p>
      </div>
    );
  if (isPending) return <Loading page />;
  if (!b)
    return (
      <div className="center">
        <p>
          Dit gebouw bestaat niet of je hebt er geen toegang toe. <Link to="/">Terug naar gebouwen</Link>
        </p>
      </div>
    );

  const plan = floor?.plan ?? null;
  const pages = plan?.pages ?? 0;
  const busyPin = Boolean(draw);

  const detailActions = {
    onClose: () => {
      setSel(null);
      setHl(null);
    },
    onEdit: (d: IssueDetail) => setModal({ type: "issue", issue: d }),
    onShow: (loc: { floorId: string; x: number; y: number }, issueId: string) => {
      if (loc.floorId !== floorId) setFloorId(loc.floorId);
      setView("plan");
      setMpane("plan");
      setHl(issueId);
      if (window.innerWidth <= 760) setSel(null);
      setFocus((cur) => ({ x: loc.x, y: loc.y, n: (cur?.n ?? 0) + 1 }));
    },
    onMovePin: (d: IssueDetail) => {
      const main = d.locations[0];
      if (main && main.floorId !== floorId) setFloorId(main.floorId);
      setView("plan");
      setMpane("plan");
      setDraw(null);
      setTapMode({ kind: "move", issue: d });
      if (window.innerWidth <= 760) setSel(null);
    },
    onAddLocation: (d: IssueDetail) => setModal({ type: "addLocation", issue: d }),
    onLinkTask: (d: IssueDetail) => setModal({ type: "task", forIssue: d.id }),
    onOpenTasks: () => {
      setView("tasks");
      if (window.innerWidth <= 760) setSel(null);
    },
  };

  /** Zet de laag aan waarop deze asset staat (Meters of Assets). */
  const showLayerOf = (a: { assetKind?: string | null }) => {
    const key = meterLayer && a.assetKind === "meter" ? "meters" : "assets";
    if (!layers[key]) setLayers({ ...layers, [key]: true });
  };
  /**
   * Bulkactie uit de assetlijst: de gekozen assets één voor één op de tekening plaatsen. Elke stap opent
   * de verdieping waar de asset al staat; zonder verdieping blijft de huidige (of de eerste met tekening).
   */
  function placeMany(list: AssetDto[]) {
    if (!b || !list.length) return;
    const items: PlaceItem[] = list.map((a) => ({ id: a.id, code: a.code, name: a.name, floorId: a.floorId }));
    const first = items[0]!.floorId ?? (plan ? floorId : (b.floors.find((x) => x.plan)?.id ?? floorId));
    if (first && first !== floorId) setFloorId(first);
    setView("plan");
    setMpane("plan");
    setDraw(null);
    setMapMenu(null);
    setSelAsset(null);
    const want = { ...layers };
    for (const a of list) want[meterLayer && a.assetKind === "meter" ? "meters" : "assets"] = true;
    setLayers(want);
    setTapMode({ kind: "placeAsset", asset: items[0]!, queue: items.slice(1), step: { n: 1, total: items.length } });
  }
  const assetActions: AssetActions = {
    onClose: () => setSelAsset(null),
    onEdit: (a) => setModal({ type: "asset", id: a.id }),
    onOpenAsset: openAsset,
    onNewPart: (a) =>
      setModal({
        type: "asset",
        initial: {
          parentAssetId: a.id,
          elementCode: a.elementCode,
          assetKind: a.assetKind,
          floorId: floorId || null,
        },
      }),
    onShow: (a) => {
      if (a.floorId && a.floorId !== floorId) setFloorId(a.floorId);
      setView("plan");
      setMpane("plan");
      showLayerOf(a);
      if (a.x != null && a.y != null) setFocus((cur) => ({ x: a.x!, y: a.y!, n: (cur?.n ?? 0) + 1 }));
    },
    onPlace: (a) => {
      if (a.floorId && a.floorId !== floorId) setFloorId(a.floorId);
      setView("plan");
      setMpane("plan");
      setDraw(null);
      setMapMenu(null);
      showLayerOf(a);
      setTapMode({ kind: "placeAsset", asset: { id: a.id, code: a.code } });
    },
    onPlaceCopies: (copyFloor, copies) => {
      if (!copies.length) return;
      if (copyFloor && copyFloor !== floorId) setFloorId(copyFloor);
      setView("plan");
      setMpane("plan");
      setDraw(null);
      setMapMenu(null);
      setSelAsset(null);
      if (!layers.assets) setLayers({ ...layers, assets: true });
      setTapMode({
        kind: "placeAsset",
        asset: copies[0]!,
        queue: copies.slice(1),
        step: { n: 1, total: copies.length },
      });
    },
    onNewIssue: (a) =>
      setModal({
        type: "issue",
        initial: {
          floorId: a.floorId ?? floorId,
          roomId: a.roomId,
          installationId: a.id,
          pin: a.floorId && a.x != null && a.y != null ? { x: a.x, y: a.y } : null,
        },
      }),
    onOpenIssue: openIssue,
  };
  // Weergave-opties van de tekening: op desktop in de werkbalk, op de telefoon in een sheet.
  type LayerKey = "issues" | "assets" | "meters" | "rooms";
  const layerItems: ChoiceItem<LayerKey>[] = [
    ...(seeIssues && seeAssets
      ? [
          { value: "issues" as const, label: "Pins", pressed: layers.issues, title: "Pins van bevindingen" },
          {
            value: "assets" as const,
            label: "Assets",
            icon: <IconAssets size={18} aria-hidden="true" />,
            pressed: layers.assets,
          },
        ]
      : []),
    ...(meterLayer && b.assets.some((a) => a.assetKind === "meter")
      ? [
          {
            value: "meters" as const,
            label: "Meters",
            pressed: layers.meters,
            title: "Meters op de tekening",
          },
        ]
      : []),
    ...(floorRooms.some((r) => r.shape)
      ? [
          {
            value: "rooms" as const,
            label: "Ruimtes",
            icon: <IconLayers size={18} aria-hidden="true" />,
            pressed: showRooms,
            title: "Ingetekende ruimtes tonen",
          },
        ]
      : []),
  ];
  const viewLayers = (
    <>
      {plan && layerItems.length > 0 && (
        <Segmented
          className="layers"
          label="Lagen op de tekening"
          items={layerItems}
          onChange={(k) => (k === "rooms" ? setShowRooms(!showRooms) : setLayers({ ...layers, [k]: !layers[k] }))}
        />
      )}
      {plan && seeAssets && layers.assets && b.assets.some((a) => a.obligationStatus) && (
        <button
          type="button"
          className="btn small"
          aria-pressed={onlyDue}
          onClick={() => setOnlyDue(!onlyDue)}
          title="Alleen assets met een verlopen of bijna verlopen keuring"
        >
          Alleen verlopen en binnenkort
        </button>
      )}
      {plan && seeAssets && seeCondition && layers.assets && (
        <Segmented
          label="Conditie op de tekening"
          items={[
            { value: "off", label: "Geen conditie" },
            { value: "measured", label: "Gemeten" },
            { value: "theoretical", label: "Theoretisch" },
          ]}
          value={condView}
          onChange={setCondView}
        />
      )}
    </>
  );
  const viewZoom = plan && (
    <>
      {plan && (
        <span className="zoom" role="group" aria-label="Zoom">
          <button
            type="button"
            className="btn small ghost"
            aria-label="Uitzoomen"
            onClick={() => requestZoom(Math.max(ZMIN, zoom / 1.25))}
          >
            <IconZoomOut size={18} aria-hidden="true" />
          </button>
          <button type="button" className="btn small ghost num" title="Passend maken" onClick={() => setZoom(1)}>
            {Math.round(zoom * 100)}%
          </button>
          <button
            type="button"
            className="btn small ghost"
            aria-label="Inzoomen"
            onClick={() => requestZoom(Math.min(ZMAX, zoom * 1.25))}
          >
            <IconZoomIn size={18} aria-hidden="true" />
          </button>
        </span>
      )}
    </>
  );
  const hasViewLayers = Boolean(plan && (seeAssets || floorRooms.some((r) => r.shape)));
  const hasViewManage = Boolean(plan && (canManage || canCreate));
  const viewManage = (
    <>
      {plan && pages > 1 && canManage && (
        <label className="hint">
          Blad{" "}
          <select
            value={floor!.planPage}
            onChange={(e) => setPage(Number(e.target.value))}
            aria-label="Blad van de PDF"
          >
            {Array.from({ length: pages }, (_, k) => (
              <option key={k + 1}>{k + 1}</option>
            ))}
          </select>
        </label>
      )}
      {plan && canManage && canUpload && (
        <button
          type="button"
          className="btn small"
          onClick={() => {
            closeViewSheet();
            replaceInput.current?.click();
          }}
          title="Tekening vervangen (PDF of afbeelding)"
        >
          Tekening vervangen
        </button>
      )}
      {plan && canManage && (
        <button
          type="button"
          className="btn small"
          aria-pressed={draw?.kind === "draw" && draw.batch}
          onClick={() => {
            closeViewSheet();
            startBatch();
          }}
          title="Vakken tekenen en direct een naam geven"
        >
          Ruimtes intekenen
        </button>
      )}
      {plan && canManage && (
        <button
          type="button"
          className="btn small"
          aria-pressed={draw?.kind === "scale"}
          onClick={() => {
            closeViewSheet();
            startScale();
          }}
          title="Een bekende maat aantikken, zodat de app de m² van ruimtes kan berekenen"
        >
          {floor?.scale ? "Schaal" : "Schaal instellen"}
        </button>
      )}
      {plan && canCreate && (
        <button
          type="button"
          className="btn small"
          aria-pressed={tapMode?.kind === "pin"}
          onClick={() => {
            closeViewSheet();
            setDraw(null);
            setMapMenu(null);
            setTapMode(tapMode?.kind === "pin" ? null : { kind: "pin" });
          }}
        >
          Pin plaatsen
        </button>
      )}
    </>
  );

  const detail =
    selAsset === BUILDING_OWNER ? (
      <BuildingObligationsPanel building={b} onClose={() => setSelAsset(null)} />
    ) : selAsset ? (
      <AssetPanel key={selAsset} building={b} assetId={selAsset} actions={assetActions} />
    ) : sel ? (
      <IssueDetailPanel
        building={b}
        issueId={sel}
        tasks={allTasks}
        actions={{ ...detailActions, onOpenAsset: openAsset }}
      />
    ) : null;
  const newIssue = () => {
    if (!b.floors.length) return toast("Voeg eerst een verdieping toe.");
    setModal({ type: "issue", initial: { floorId } });
  };
  // Op telefoon met tekening: eerst de plek aantikken (zoals de oude "+ Pin bevinding").
  newIssueRef.current = () => {
    if (!canCreate) return;
    if (window.innerWidth <= 760 && plan && view === "plan") {
      setMpane("plan");
      setTapMode({ kind: "pin" });
    } else newIssue();
  };

  return (
    <div
      className={`buildingpage view-${view === "plan" ? "plan" : "table"} m-${mpane} ${sel || selAsset ? "has-sel" : ""}`}
    >
      <div className="bbar">
        <strong className="bsection show-mobile">{SECTION_TITLE[section]}</strong>
        {/* Bij een lijst kies je de plek hier; bij de tekening met het liftpaneel. */}
        {PLACE_SECTIONS.has(section) && b.floors.length > 0 && (
          <PlaceBar
            building={b}
            floorId={placeFloor}
            roomId={placeRoom}
            onFloor={pickPlaceFloor}
            onRoom={pickPlaceRoom}
            open={openCounts}
            empty={section === "bevindingen" && listForPane.length === 0}
          />
        )}
        <span className="grow" />
        <div
          className={`kpis ${f.prio ? "filtered" : ""}`}
          role="group"
          aria-label="Actieve bevindingen per prioriteit"
          hidden={!seeIssues}
        >
          {PRIORITIES.map((p) => (
            <button
              type="button"
              key={p}
              className="kpi"
              aria-pressed={f.prio === p}
              title={`${PRIO[p].l}: ${PRIO[p].t}`}
              onClick={() => setF({ ...f, prio: f.prio === p ? "" : p, status: "actief" })}
            >
              <i className={`prio-${p}`} aria-hidden="true" />
              <b className="num">{counts[p]}</b> {p}
            </button>
          ))}
        </div>
        {canReport && (
          <button
            type="button"
            className="btn small reportbtn"
            title="PDF-rapport"
            onClick={() => setModal({ type: "report" })}
          >
            <IconFile size={18} aria-hidden="true" />
            PDF-rapport
          </button>
        )}
      </div>

      {view !== "plan" ? (
        <div className="tlayout">
          {view === "assets" ? (
            <div className="assetsview">
              {assetMode === "list" ? (
                <AssetList
                  key={section}
                  list={section === "arbeidsmiddelen" ? "equipment" : section === "meters" ? "meters" : "assets"}
                  building={b}
                  floorId={placeFloor}
                  roomId={placeRoom}
                  selected={selAsset}
                  onOpen={openAsset}
                  onPlaceMany={placeMany}
                  onNew={() =>
                    setModal({
                      type: "asset",
                      initial: {
                        floorId: floorId || null,
                        ...(placeRoom ? { roomId: placeRoom } : {}),
                        ...(section === "arbeidsmiddelen"
                          ? { assetKind: "equipment" as const }
                          : section === "meters"
                            ? { assetKind: "meter" as const }
                            : {}),
                      },
                    })
                  }
                >
                  {section === "meters" && <MeterRoundCard buildingId={b.id} />}
                </AssetList>
              ) : (
                <ObligationList
                  buildingId={b.id}
                  onOpenAsset={(r) => openAsset(r.asset?.id ?? BUILDING_OWNER)}
                  assetFilter={placeFloor ? assetInPlace : undefined}
                  head={{
                    label: `${b.name}${placeLabel ? ` · ${placeLabel}` : ""}`,
                    actions: (
                      <button
                        type="button"
                        className="btn"
                        title="Onderhoud en keuringen van het gebouw zelf, zonder asset"
                        onClick={() => openAsset(BUILDING_OWNER)}
                      >
                        <IconBuilding size={18} aria-hidden="true" />
                        Bij het gebouw
                      </button>
                    ),
                  }}
                />
              )}
            </div>
          ) : view === "table" ? (
            <IssueTable
              building={b}
              list={listForPane}
              floorName={placeLabel}
              f={f}
              setF={setF}
              selected={sel}
              onOpen={openIssue}
            />
          ) : (
            <TaskBoard
              building={b}
              tasks={allTasks}
              issues={allIssues}
              onOpenIssue={openIssue}
              onEdit={(t) => setModal({ type: "task", task: t })}
              onNew={() => setModal({ type: "task" })}
            />
          )}
          {detail && <div className="tdetail">{detail}</div>}
        </div>
      ) : (
        <>
          <section className="planwrap" aria-label="Tekening">
            <div className="plantools">
              {phone && b.floors.length > 1 ? (
                <FloorLift building={b} floorId={floorId} onFloor={setFloorId} open={openCounts} phone />
              ) : (
                floor && <strong className="liftname ellipsis">{floor.name}</strong>
              )}
              <span className="grow" />
              {phone ? (
                plan && (
                  <button
                    type="button"
                    className="btn small"
                    aria-expanded={viewSheet}
                    aria-controls={viewSheet ? viewSheetId : undefined}
                    aria-haspopup="dialog"
                    onClick={() => setViewSheet(true)}
                    aria-label="Weergave"
                    title="Weergave"
                  >
                    <IconLayers size={18} aria-hidden="true" />
                    {/* Bij drie verdiepingen alleen het icoon (container-query), zodat de verdiepingen passen. */}
                    <span className={b.floors.length > 2 ? "toollabel" : undefined}>Weergave</span>
                  </button>
                )
              ) : (
                <>
                  {hasViewLayers && (
                    <ToolPopover
                      label="Weergave"
                      title="Weergave"
                      icon={<IconLayers size={18} aria-hidden="true" />}
                      open={viewPop}
                      onOpenChange={setViewPop}
                    >
                      <div className="viewsheet">{viewLayers}</div>
                    </ToolPopover>
                  )}
                  {viewZoom}
                  {hasViewManage && (
                    <ToolPopover
                      label="Meer acties voor de tekening"
                      title="Acties tekening"
                      icon={<IconMore size={18} aria-hidden="true" />}
                      hideLabel
                      open={actionsPop}
                      onOpenChange={setActionsPop}
                    >
                      <div className="viewsheet">{viewManage}</div>
                    </ToolPopover>
                  )}
                </>
              )}
            </div>
            {/* Tijdens intekenen of schaal instellen niet: de balk erboven heeft dan de knoppen. */}
            {!phone && !draw && (
              <FloorLift building={b} floorId={floorId} onFloor={setFloorId} open={openCounts} phone={false} />
            )}
            {phone && viewSheet && (
              <Modal id={viewSheetId} title="Weergave" onClose={closeViewSheet}>
                <div className="viewsheet">
                  <div className="viewgroup">{viewLayers}</div>
                  <div className="viewgroup">{viewZoom}</div>
                  {canManage || canCreate ? <div className="viewgroup">{viewManage}</div> : null}
                </div>
              </Modal>
            )}

            {tapMode && !draw && (
              <div className="pinbar" role="status">
                <span>
                  {tapMode.kind === "pin"
                    ? "Tik op de tekening waar je de bevinding ziet"
                    : tapMode.kind === "move"
                      ? `Tik op de nieuwe plek voor ${tapMode.issue.code}`
                      : tapMode.kind === "placeAsset"
                        ? `Tik op de tekening waar ${tapMode.asset.code}${tapMode.asset.name ? ` ${tapMode.asset.name}` : ""} staat${tapMode.step ? ` (${tapMode.step.n} van ${tapMode.step.total})` : ""}`
                        : `Tik waar ${tapMode.issue.code} ook voorkomt. Wissel gerust van verdieping.`}
                </span>
                <span className="acts">
                  {tapMode.kind === "placeAsset" && tapMode.queue?.length ? (
                    <button type="button" onClick={() => setTapMode(nextCopy(tapMode.queue!, tapMode.step))}>
                      Overslaan
                    </button>
                  ) : null}
                  <button type="button" onClick={() => setTapMode(null)}>
                    {tapMode.kind === "addloc" || (tapMode.kind === "placeAsset" && tapMode.step)
                      ? "Klaar"
                      : "Annuleren"}
                  </button>
                </span>
              </div>
            )}

            {draw?.kind === "scale" && (
              <ScaleBar
                draw={draw}
                setDraw={setDraw}
                metersInput={metersInput}
                hadScale={Boolean(floor?.scale)}
                aspect={planAspect}
                areas={areas}
                roomCount={floorRooms.length}
                onAdd={addScaleLine}
                onSave={saveScale}
              />
            )}
            {draw && draw.kind !== "scale" && (
              <DrawBar
                draw={draw}
                setDraw={setDraw}
                nameInput={nameInput}
                onSaveName={saveBatchRoom}
                onFinish={finishPoly}
                onSaveVertices={() => draw.kind === "vertices" && saveShape(draw.roomId, draw.points, draw.roomName)}
              />
            )}

            {!floor ? (
              <div className="emptyplan">
                <p>
                  Dit gebouw heeft nog geen verdiepingen. Sleep de plattegronden hierheen: elk bestand wordt een
                  verdieping.
                </p>
                {canManage && (
                  <div className="btnrow center-row">
                    <button
                      type="button"
                      className="btn primary"
                      onClick={() => setModal({ type: "import", files: [] })}
                    >
                      Tekeningen importeren
                    </button>
                    <button type="button" className="btn" onClick={() => setModal({ type: "floor" })}>
                      + Verdieping zonder tekening
                    </button>
                  </div>
                )}
              </div>
            ) : !plan ? (
              <div className="emptyplan">
                <p>
                  <strong>Nog geen tekening voor {floor.name}.</strong>
                  <br />
                  Upload een PDF of afbeelding van de plattegrond. Daarna tik je op de tekening om een bevinding vast te
                  pinnen.
                </p>
                {canManage && canUpload && (
                  <div className="btnrow center-row">
                    <button type="button" className="btn primary" onClick={() => replaceInput.current?.click()}>
                      Tekening uploaden
                    </button>
                    <button type="button" className="btn" onClick={() => setModal({ type: "import", files: [] })}>
                      Meerdere tekeningen importeren
                    </button>
                  </div>
                )}
                <p className="hint">
                  Zonder tekening kun je bevindingen ook gewoon per ruimte of installatie vastleggen.
                </p>
              </div>
            ) : (
              <PlanStage
                key={`${floor.id}|${plan.id}|${floor.planPage}`}
                floor={{ ...floor, plan }}
                rooms={floorRooms}
                zoom={zoom}
                onZoom={setZoom}
                showRooms={showRooms || busyPin}
                draw={draw}
                setDraw={setDraw}
                onShape={onShape}
                roomAreas={areas?.byRoom}
                onAspect={setPlanAspect}
                pins={draw || !layers.issues || !seeIssues ? [] : pins}
                assets={draw ? [] : assetMarkers}
                onAssetClick={openAsset}
                ghost={mapMenu ?? (modal?.type === "issue" && modal.initial?.pin ? modal.initial.pin : null)}
                onTap={onTap}
                onPinClick={openIssue}
                focus={focus}
                placing={Boolean(tapMode)}
                menu={
                  mapMenu && {
                    x: mapMenu.x,
                    y: mapMenu.y,
                    content: (
                      <>
                        <div className="hd">{floorRooms.find((r) => r.id === mapMenu.roomId)?.name ?? floor.name}</div>
                        {canCreate && (
                          <button
                            type="button"
                            role="menuitem"
                            autoFocus
                            onClick={() => {
                              const m = mapMenu;
                              setMapMenu(null);
                              setModal({
                                type: "issue",
                                initial: { floorId, roomId: m.roomId, pin: { x: m.x, y: m.y } },
                              });
                            }}
                          >
                            + Nieuwe bevinding hier
                          </button>
                        )}
                        {canAsset && (
                          <button
                            type="button"
                            role="menuitem"
                            autoFocus={!canCreate}
                            onClick={() => {
                              const m = mapMenu;
                              setMapMenu(null);
                              setModal({
                                type: "asset",
                                initial: { floorId, roomId: m.roomId, x: m.x, y: m.y },
                              });
                            }}
                          >
                            + Asset hier
                          </button>
                        )}
                        {canEditIssue && (
                          <button
                            type="button"
                            role="menuitem"
                            onClick={() => {
                              const m = mapMenu;
                              setMapMenu(null);
                              setModal({ type: "pickIssue", loc: { floorId, roomId: m.roomId, x: m.x, y: m.y } });
                            }}
                          >
                            Bestaande bevinding komt hier ook voor
                          </button>
                        )}
                        <button type="button" role="menuitem" className="mm-x" onClick={() => setMapMenu(null)}>
                          Annuleren
                        </button>
                      </>
                    ),
                  }
                }
              />
            )}
          </section>

          <IssueListPane
            building={b}
            list={listForPane}
            floorName={placeLabel ?? null}
            withScope={!PLACE_SECTIONS.has(section)}
            f={f}
            setF={setF}
            selected={sel}
            onOpen={openIssue}
          >
            {detail}
          </IssueListPane>
        </>
      )}

      <input
        ref={replaceInput}
        type="file"
        accept="application/pdf,image/png,image/jpeg,image/webp"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          if (f) void replacePlan(f);
        }}
      />

      {dropCover && !modal && (
        <div className="dropcover" aria-hidden="true">
          <span>Laat los om tekeningen te importeren</span>
        </div>
      )}

      {modal?.type === "building" && (
        <BuildingDialog
          building={b}
          onClose={() => setModal(null)}
          onSaved={(nb) => {
            cache.set(nb);
            setModal(null);
            toast("Gebouw bijgewerkt");
          }}
          onRemoved={
            canManage
              ? () => {
                  setModal(null);
                  void qc.invalidateQueries({ queryKey: ["buildings"] });
                  qc.removeQueries({ queryKey: buildingKey(b.id) });
                  navigate("/", { replace: true });
                }
              : undefined
          }
        />
      )}
      {modal?.type === "floor" && (
        <FloorDialog
          building={b}
          floorId={modal.id}
          onClose={() => setModal(null)}
          onSaved={(nb, newId) => {
            cache.set(nb);
            setModal(null);
            if (newId) setFloorId(newId);
          }}
        />
      )}
      {modal?.type === "addRoom" && floor && (
        <AddRoomDialog
          floorId={floor.id}
          hasPlan={Boolean(plan)}
          onClose={() => setModal(null)}
          onSaved={(r, drawNow) => {
            cache.upsertRoom(r);
            setModal(null);
            if (drawNow) setDraw(newDraw(r));
          }}
        />
      )}
      {modal?.type === "room" &&
        (() => {
          const room = b.rooms.find((r) => r.id === modal.id);
          if (!room) return null;
          const roomFloor = b.floors.find((f) => f.id === room.floorId);
          return (
            <RoomDialog
              building={b}
              room={room}
              canDraw={Boolean(roomFloor?.plan)}
              onClose={() => setModal(null)}
              onDraw={() => {
                setModal(null);
                setFloorId(room.floorId);
                setTimeout(() => setDraw(newDraw(room)), 0);
              }}
              onEditVertices={() => {
                setModal(null);
                setFloorId(room.floorId);
                setTimeout(
                  () =>
                    setDraw({
                      kind: "vertices",
                      roomId: room.id,
                      roomName: room.name,
                      points: toPoints(room.shape!.points),
                      active: null,
                    }),
                  0,
                );
              }}
              onAddInstallation={() => setModal({ type: "asset", initial: { floorId: room.floorId, roomId: room.id } })}
              onEditInstallation={(iid) => {
                setModal(null);
                openAsset(iid);
              }}
            />
          );
        })()}
      {modal?.type === "asset" && (
        <AssetDialog
          building={b}
          asset={b.assets.find((x) => x.id === modal.id)}
          initial={modal.initial}
          onClose={() => setModal(null)}
          onSaved={(a) => {
            void qc.invalidateQueries({ queryKey: assetKey(a.id) });
            if (!modal.id) openAsset(a.id);
          }}
        />
      )}
      {modal?.type === "issue" && (
        <IssueForm
          building={b}
          issue={modal.issue}
          initial={modal.initial}
          onClose={() => setModal(null)}
          onSaved={(d, queued) => savedIssue(d, !modal.issue, queued)}
        />
      )}
      {modal?.type === "addLocation" && (
        <AddLocationDialog
          building={b}
          issue={modal.issue}
          floorId={floorId}
          onClose={() => setModal(null)}
          onSaved={(d) => {
            issueCache.put(d);
            setModal(null);
          }}
          onPointOnPlan={(fl) => {
            const issue = modal.issue;
            setModal(null);
            if (fl !== floorId) setFloorId(fl);
            setView("plan");
            setMpane("plan");
            if (window.innerWidth <= 760) setSel(null);
            setTapMode({ kind: "addloc", issue });
          }}
        />
      )}
      {modal?.type === "pickIssue" && (
        <PickIssueDialog
          building={b}
          issues={allIssues}
          loc={modal.loc}
          onClose={() => setModal(null)}
          onSaved={(d) => {
            issueCache.put(d);
            setModal(null);
            openIssue(d.id);
          }}
        />
      )}
      {modal?.type === "task" && (
        <TaskDialog
          building={b}
          issues={allIssues}
          tasks={allTasks}
          task={modal.task}
          forIssue={modal.forIssue}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.type === "report" && (
        <ReportDialog
          building={b}
          issues={allIssues.filter((i) => !isLocal(i))}
          tasks={allTasks}
          filters={f}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.type === "import" && (
        <ImportDialog
          building={b}
          initialFiles={modal.files}
          onClose={() => setModal(null)}
          onDone={(nb, first) => {
            cache.set(nb);
            setModal(null);
            if (first) setFloorId(first);
          }}
          onPartial={(nb) => cache.set(nb)}
        />
      )}
    </div>
  );
}

/** Balk boven de tekening tijdens intekenen (teksten uit het prototype). */
function DrawBar({
  draw,
  setDraw,
  nameInput,
  onSaveName,
  onFinish,
  onSaveVertices,
}: {
  draw: Exclude<DrawState, SetScale>;
  setDraw: (d: DrawState | null) => void;
  nameInput: React.RefObject<HTMLInputElement | null>;
  onSaveName: () => void;
  onFinish: () => void;
  onSaveVertices: () => void;
}) {
  if (draw.kind === "vertices") {
    return (
      <div className="pinbar" role="status">
        <span>Versleep de hoekpunten van “{draw.roomName}” (of kies een punt en gebruik de pijltjestoetsen)</span>
        <span className="acts">
          <button type="button" onClick={onSaveVertices}>
            Opslaan
          </button>
          <button type="button" onClick={() => setDraw(null)}>
            Annuleren
          </button>
        </span>
      </div>
    );
  }
  const n = draw.pts.length;
  if (draw.batch && draw.naming) {
    return (
      <form
        className="pinbar"
        onSubmit={(e) => {
          e.preventDefault();
          onSaveName();
        }}
      >
        <label htmlFor="dr-name">Naam van deze ruimte</label>
        <span className="acts">
          <input
            id="dr-name"
            ref={nameInput}
            className="barinput"
            value={draw.nameDraft}
            placeholder="bijv. Kantoor 2.04"
            autoComplete="off"
            onChange={(e) => setDraw({ ...draw, nameDraft: e.target.value })}
          />
          <button type="submit">Opslaan &amp; volgende</button>
          <button type="button" onClick={() => setDraw({ ...draw, naming: false, shape: null, pts: [], rect: null })}>
            Opnieuw tekenen
          </button>
          <button type="button" onClick={() => setDraw(null)}>
            Stoppen
          </button>
        </span>
      </form>
    );
  }
  const subject = draw.batch ? "" : ` van “${draw.roomName}”`;
  const msg =
    draw.mode === "rect"
      ? draw.batch
        ? "Sleep een vak over de volgende ruimte"
        : `Sleep een rechthoek over “${draw.roomName}”`
      : n === 0
        ? draw.batch
          ? "Tik de eerste hoek van een ruimte aan"
          : `Tik de eerste hoek${subject} aan`
        : n < 3
          ? `Tik de volgende hoek aan (${n} punt${n > 1 ? "en" : ""})`
          : `${n} punten · tik het eerste punt of “Klaar”${draw.batch ? "" : " om te sluiten"}`;
  return (
    <div className="pinbar" role="status">
      <span>
        {msg}
        {draw.batch && draw.count > 0 && ` · ${draw.count} ruimte${draw.count > 1 ? "s" : ""} toegevoegd`}
      </span>
      <span className="acts">
        <span className="seg" role="group" aria-label="Tekenwijze">
          <button
            type="button"
            aria-pressed={draw.mode === "poly"}
            onClick={() => setDraw({ ...draw, mode: "poly", pts: [], rect: null, hover: null })}
          >
            Punten
          </button>
          <button
            type="button"
            aria-pressed={draw.mode === "rect"}
            onClick={() => setDraw({ ...draw, mode: "rect", pts: [], rect: null, hover: null })}
          >
            Rechthoek
          </button>
        </span>
        {draw.mode === "poly" && (
          <>
            <button type="button" disabled={!n} onClick={() => setDraw({ ...draw, pts: draw.pts.slice(0, -1) })}>
              Punt terug
            </button>
            <button type="button" disabled={n < 3} onClick={onFinish}>
              Klaar
            </button>
          </>
        )}
        <button type="button" onClick={() => setDraw(null)}>
          {draw.batch && draw.count ? "Stoppen" : "Annuleren"}
        </button>
      </span>
    </div>
  );
}

/** Balk boven de tekening tijdens "Schaal instellen": maat aantikken → lengte invullen → opslaan. */
function ScaleBar({
  draw,
  setDraw,
  metersInput,
  hadScale,
  aspect,
  areas,
  roomCount,
  onAdd,
  onSave,
}: {
  draw: SetScale;
  setDraw: (d: DrawState | null) => void;
  metersInput: React.RefObject<HTMLInputElement | null>;
  hadScale: boolean;
  aspect: number | null;
  areas: ReturnType<typeof floorAreas> | null;
  roomCount: number;
  onAdd: (d: SetScale) => void;
  onSave: (d: SetScale) => void;
}) {
  const n = draw.lines.length;
  const cancel = (
    <button type="button" onClick={() => setDraw(null)}>
      Annuleren
    </button>
  );
  if (draw.pts.length === 2) {
    return (
      <form
        className="pinbar"
        onSubmit={(e) => {
          e.preventDefault();
          onAdd(draw);
        }}
      >
        <label htmlFor="sc-meters">Werkelijke lengte van deze maat (m)</label>
        <span className="acts">
          <input
            id="sc-meters"
            ref={metersInput}
            className="barinput num"
            inputMode="decimal"
            autoComplete="off"
            autoFocus
            placeholder="bijv. 12,5"
            value={draw.meters}
            onChange={(e) => setDraw({ ...draw, meters: e.target.value })}
          />
          <button type="submit">Toevoegen</button>
          <button type="button" onClick={() => setDraw({ ...draw, pts: [], meters: "" })}>
            Opnieuw aantikken
          </button>
          {cancel}
        </span>
      </form>
    );
  }
  if (draw.pts.length === 1) {
    return (
      <div className="pinbar" role="status">
        <span>Tik het eind van de maat aan</span>
        <span className="acts">
          <button type="button" onClick={() => setDraw({ ...draw, pts: [] })}>
            Punt terug
          </button>
          {cancel}
        </span>
      </div>
    );
  }
  const dev = scaleDeviation(n && aspect ? { aspect, lines: draw.lines } : null);
  const msg =
    n === 0
      ? "Tik het begin aan van een maat die je kent, bijvoorbeeld een gevel of lange muur"
      : `Schaal: ${draw.lines.map((l) => formatMeters(l.meters)).join(" en ")}${
          areas ? ` · ${formatM2(areas.total)} (${areas.measured} van ${roomCount} ruimtes ingetekend)` : ""
        }${n === 1 ? ". Tik eventueel een tweede maat in de andere richting aan als controle." : ""}`;
  return (
    <div className="pinbar" role="status">
      <span>
        {msg}
        {dev != null && (
          <>
            {" · "}
            <span className={dev > 0.05 ? "scalewarn" : undefined}>
              maten wijken {(Math.round(dev * 1000) / 10).toLocaleString("nl-NL")} % af
              {dev > 0.05 ? ", controleer de punten en lengtes" : ""}
            </span>
          </>
        )}
      </span>
      <span className="acts">
        {n > 0 && (
          <button type="button" onClick={() => onSave(draw)}>
            Opslaan
          </button>
        )}
        {n > 0 && (
          <button type="button" onClick={() => setDraw({ ...draw, lines: [], pts: [] })}>
            Opnieuw
          </button>
        )}
        {hadScale && n === 0 && (
          <button type="button" onClick={() => onSave(draw)}>
            Schaal wissen
          </button>
        )}
        {cancel}
      </span>
    </div>
  );
}
